/** Resolve product id for /edit-product/:id across admin API shapes (orphans use product_id). */
export const resolveEditProductId = (product) => {
  if (!product) return null;

  const id =
    product.productId ??
    product.product_id ??
    product.id ??
    product.ProductMkt ??
    (product.gtin
      ? `${product.gtin}-${
          product.variant != null && String(product.variant) !== ""
            ? String(product.variant).padStart(3, "0")
            : "000"
        }`
      : null);

  if (id == null || id === "" || String(id) === "undefined") return null;
  return String(id);
};

export const withResolvedEditProductId = (product) => {
  if (!product) return product;
  const productId = resolveEditProductId(product);
  if (!productId || product.productId === productId) return product;
  return { ...product, productId };
};

export const PRODUCT_STATUS_OPTIONS = [
  { id: "ACTIVE", name: "פעיל" },
  { id: "ACTIVE_UNTIL_OOS", name: "פעיל עד גמר המלאי" },
  { id: "INACTIVE", name: "לא פעיל" },
];

const PRODUCT_STATUS_IDS = new Set(PRODUCT_STATUS_OPTIONS.map((o) => o.id));

export const isListedProductStatus = (status) =>
  status === "ACTIVE" || status === "ACTIVE_UNTIL_OOS";

export const resolveProductActiveStatus = (product) => {
  const candidates = [
    product?.productStatus,
    product?.status,
    product?.categories?.[0]?.productStatus,
  ];
  return candidates.find((s) => PRODUCT_STATUS_IDS.has(s)) || null;
};

export const oppositeProductStatus = (status) =>
  status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

export const productStatusLabel = (status) =>
  PRODUCT_STATUS_OPTIONS.find((option) => option.id === status)?.name ?? "-";

const getProductRecord = (productOrRow) =>
  productOrRow?.product || productOrRow;

/** Category path for admin table rows and catalog product shapes. */
export const getProductCategoryHierarchy = (productOrRow) => {
  const entry =
    productOrRow?.categories?.[0] ??
    productOrRow?.product?.categories?.[0] ??
    null;

  if (!entry) return [];

  if (
    Array.isArray(entry?.categoryHierarchy) &&
    entry.categoryHierarchy.length
  ) {
    return entry.categoryHierarchy;
  }

  if (Array.isArray(entry) && entry.length > 0 && entry[0]?.slug) {
    return entry;
  }

  if (entry?.slug) return [entry];

  return [];
};

export const productHasDepartment = (productOrRow) =>
  getProductCategoryHierarchy(productOrRow).some((cat) => Boolean(cat?.slug));

export const productHasDefinedName = (productOrRow) => {
  const name = getProductRecord(productOrRow)?.name;
  return typeof name === "string" && name.trim() !== "";
};

export const productHasDefinedPrice = (productOrRow) => {
  const price =
    productOrRow?.pricing?.price ??
    productOrRow?.product?.pricing?.price ??
    null;
  if (price == null || price === "") return false;
  return Number.isFinite(Number(price));
};

/** Listed statuses require name, price, and department. Deactivation is always allowed
 * so a mis-configured live product can still be taken offline.
 * `categorySlug` counts as department when the product has no categories path
 * (e.g. raw nodes on the admin category page).
 */
export const canUpdateProductStatus = (
  productOrRow,
  categorySlug,
  targetStatus,
) => {
  if (targetStatus && !isListedProductStatus(targetStatus)) return true;
  return (
    productHasDefinedName(productOrRow) &&
    productHasDefinedPrice(productOrRow) &&
    (productHasDepartment(productOrRow) || Boolean(categorySlug))
  );
};

export const getProductStatusUpdateBlockers = (
  productOrRow,
  categorySlug,
  targetStatus,
) => {
  if (targetStatus && !isListedProductStatus(targetStatus)) return [];

  const missing = [];
  if (!productHasDefinedName(productOrRow)) missing.push("שם מוצר");
  if (!productHasDefinedPrice(productOrRow)) missing.push("מחיר");
  if (!productHasDepartment(productOrRow) && !categorySlug) {
    missing.push("מחלקה");
  }
  return missing;
};

/** Resolve category slug for update-status across catalog + admin table shapes.
 * Prefer the product's own leaf category; use categorySlug only as fallback
 * (e.g. department page slug when the product has no category path).
 */
export const resolveProductCategorySlug = (product, categorySlug) => {
  const categoryEntry = product?.categories?.[0];

  if (categoryEntry) {
    // Catalog / product-edit shape: hierarchy[0] is the leaf category
    const hierarchy = categoryEntry.categoryHierarchy;
    if (Array.isArray(hierarchy) && hierarchy.length) {
      const leaf = hierarchy.find((cat) => cat?.slug) || hierarchy[0];
      if (leaf?.slug) return leaf.slug;
    }

    // Admin / department tables: categories[0] is a path array from extractProductsFromTree.
    // The last node is the category that actually contains the product (including families).
    if (Array.isArray(categoryEntry) && categoryEntry.length) {
      for (let i = categoryEntry.length - 1; i >= 0; i--) {
        if (categoryEntry[i]?.slug) return categoryEntry[i].slug;
      }
    }

    if (typeof categoryEntry.slug === "string" && categoryEntry.slug) {
      return categoryEntry.slug;
    }
  }

  return categorySlug || null;
};

export const withProductStatus = (product, status) => {
  if (!product) return product;

  const categories = product.categories;
  let nextCategories = categories;

  if (Array.isArray(categories) && categories.length) {
    const first = categories[0];
    if (first && !Array.isArray(first) && typeof first === "object") {
      nextCategories = [
        { ...first, productStatus: status },
        ...categories.slice(1),
      ];
    }
  }

  return {
    ...product,
    status,
    productStatus: status,
    categories: nextCategories,
  };
};
